// ActionsPage — full lifecycle, evidence upload, review/verification workflow
import { useCallback, useEffect, useMemo, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import {
    Search, Plus, MoreVertical, Pencil, Loader2, X, Save,
    CheckSquare, AlertTriangle, Calendar, Sparkles, FolderOpen,
    Send, ShieldCheck, Paperclip, FileText, Users, Building2, Check, Mail, Upload, Trash2, BellRing, CheckCircle2,
} from "lucide-react";
import { toast } from "sonner";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useView } from "@/context/ViewContext";
import { useSortableRows, SortableTh } from "@/lib/sortable";
import Pill from "@/components/Pill";
import ActionDetailDialog from "@/pages/actions/ActionDetailDialog";

const PRIO_ORDER = { Critical: 0, High: 1, Medium: 2, Low: 3 };

const PRIO_TONE = { Critical: "red", High: "red", Medium: "amber", Low: "blue" };
const STATUS_TONE = { "Open": "amber", "In progress": "blue", "Blocked": "red", "Submitted": "purple", "Complete": "green" };
const PRIORITIES = ["Low", "Medium", "High", "Critical"];
const STATUSES = ["Open", "In progress", "Blocked", "Submitted", "Complete"];
const SOURCES = ["Manual", "Audit", "Inspection", "Incident", "Resident concern", "BSC mobilisation", "CDM", "Compliance", "Onboarding", "Training", "Fire safety"];
const INTERNAL_ROLES = new Set(["director", "administrator", "consultant"]);

const CAN_EDIT_ROLES = new Set(["director", "administrator", "consultant", "client_admin", "site_manager", "reviewer"]);

function isOverdue(action) {
    if (!action.due_date) return false;
    if (action.status === "Complete") return false;
    return action.due_date < new Date().toISOString().slice(0, 10);
}

export default function ActionsPage() {
    const [rows, setRows] = useState([]);
    const [clients, setClients] = useState([]);
    const [q, setQ] = useState("");
    const [statusFilter, setStatusFilter] = useState("All");
    const [showOverdueOnly, setShowOverdueOnly] = useState(false);
    const [dialog, setDialog] = useState({ open: false, mode: "create", initial: null });
    const [detail, setDetail] = useState({ open: false, id: null });
    const [returnTo, setReturnTo] = useState(null);
    const navigate = useNavigate();
    const [menuRowId, setMenuRowId] = useState(null);
    const [selected, setSelected] = useState(new Set());
    const { user } = useAuth();
    const { portalClientId, isInternalView } = useView();
    const [searchParams, setSearchParams] = useSearchParams();
    const canEdit = user && CAN_EDIT_ROLES.has(user.role);
    const isReviewer = user && INTERNAL_ROLES.has(user.role);

    const refresh = useCallback(async () => {
        const p = portalClientId && !isInternalView ? { client_id: portalClientId } : {};
        const { data } = await api.get("/actions", { params: p });
        setRows(data);
    }, [portalClientId, isInternalView]);

    useEffect(() => { api.get("/clients").then((r) => setClients(r.data)); }, []);
    useEffect(() => { refresh(); }, [refresh]);

    useEffect(() => {
        if (searchParams.get("new") === "1" && canEdit) {
            const prefill = {
                project_id: searchParams.get("project") || null,
                client_id: searchParams.get("client") || null,
                project_label: searchParams.get("project_label") || null,
            };
            setDialog({ open: true, mode: "create", initial: null, prefill });
            ["new", "project", "client", "project_label"].forEach((p) => searchParams.delete(p));
            setSearchParams(searchParams, { replace: true });
        }
        const openId = searchParams.get("open");
        if (openId) {
            setDetail({ open: true, id: openId });
            const back = searchParams.get("back");
            if (back) setReturnTo(back);
            searchParams.delete("open");
            searchParams.delete("back");
            setSearchParams(searchParams, { replace: true });
        }
    }, [searchParams, canEdit, setSearchParams]);

    const clientMap = Object.fromEntries(clients.map((c) => [c.id, c]));
    const filtered = rows.filter((a) => {
        if (statusFilter !== "All" && a.status !== statusFilter) return false;
        if (showOverdueOnly && !isOverdue(a)) return false;
        if (q && !`${a.title} ${a.assignee_name || ""} ${a.created_by_name || ""} ${clientMap[a.client_id]?.name || ""} ${a.source || ""}`
            .toLowerCase().includes(q.toLowerCase())) return false;
        return true;
    });

    const accessors = useMemo(() => ({
        title: (a) => a.title,
        client: (a) => clientMap[a.client_id]?.name || "",
        assignee_name: (a) => a.assignee_name || "",
        created_by_name: (a) => a.created_by_name || "",
        priority: (a) => PRIO_ORDER[a.priority] ?? 99,
        due_date: (a) => a.due_date || "",
        status: (a) => a.status,
    }), [clientMap]); // eslint-disable-line react-hooks/exhaustive-deps
    const { sortKey, sortDir, toggleSort, sorted } = useSortableRows(filtered, { accessors });

    const openCount = rows.filter((a) => a.status !== "Complete").length;
    const overdueCount = rows.filter(isOverdue).length;

    async function quickSubmit(action) {
        try {
            await api.post(`/actions/${action.id}/submit-review`, {});
            toast.success(`Submitted for review: ${action.title}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not submit");
        }
    }

    async function quickVerify(action) {
        try {
            await api.post(`/actions/${action.id}/verify`, {});
            toast.success(`Verified & closed: ${action.title}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not verify");
        }
    }

    async function quickComplete(action) {
        try {
            await api.post(`/actions/${action.id}/complete`, {});
            toast.success(`Marked complete: ${action.title}`);
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Could not mark complete");
        }
    }

    const toggleSelect = (id) => setSelected((prev) => {
        const n = new Set(prev);
        n.has(id) ? n.delete(id) : n.add(id);
        return n;
    });
    const visibleIds = sorted.map((a) => a.id);
    const allSelected = visibleIds.length > 0 && visibleIds.every((id) => selected.has(id));
    const toggleSelectAll = () => setSelected(allSelected ? new Set() : new Set(visibleIds));
    const clearSelection = () => setSelected(new Set());

    async function bulkDelete() {
        const ids = [...selected];
        if (ids.length === 0) return;
        if (!window.confirm(`Delete ${ids.length} action${ids.length > 1 ? "s" : ""}? This cannot be undone from the list.`)) return;
        try {
            const { data } = await api.post("/actions/bulk-delete", { ids });
            toast.success(`Deleted ${data.deleted} action${data.deleted === 1 ? "" : "s"}`);
            clearSelection();
            refresh();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Bulk delete failed");
        }
    }

    return (
        <div data-testid="actions-page" className="space-y-5">
            <div className="flex items-end justify-between flex-wrap gap-3">
                <div>
                    <span className="inline-block pb-1 text-[10.5px] font-bold tracking-[.08em] uppercase"
                          style={{ borderBottom: "2px solid var(--brand-accent, #C75A1B)", color: "#6F6A64" }}>
                        Projects & Delivery
                    </span>
                    <h2 className="text-2xl font-bold tracking-tight mt-2 text-[#1F1F1F]">Actions</h2>
                    <p className="text-[13px] mt-1" style={{ color: "#6F6A64" }}>
                        {rows.length} actions · <span className="font-semibold text-[#C68A1C]">{openCount} open</span> ·
                        {" "}<span className="font-semibold text-[#B33A2E]">{overdueCount} overdue</span>
                    </p>
                </div>
                {canEdit && (
                    <button data-testid="actions-new-button"
                        onClick={() => setDialog({ open: true, mode: "create", initial: null })}
                        className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}
                        onMouseEnter={(e) => (e.currentTarget.style.background = "#9F4212")}
                        onMouseLeave={(e) => (e.currentTarget.style.background = "var(--brand-accent, #C75A1B)")}>
                        <Plus className="w-3.5 h-3.5" /> New action
                    </button>
                )}
            </div>

            <div className="bg-white border rounded-[11px] shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                <div className="flex items-center gap-3 px-4 py-3 border-b flex-wrap" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex items-center gap-2 bg-[#F6F4F0] border rounded-[8px] px-3 py-1.5 flex-1 min-w-[220px] max-w-[340px]"
                         style={{ borderColor: "#E5E0DA" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
                        <input value={q} onChange={(e) => setQ(e.target.value)}
                            data-testid="actions-search"
                            placeholder="Search title, assignee, client, source…"
                            className="flex-1 bg-transparent outline-none text-[12.5px]" />
                    </div>
                    <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
                        data-testid="actions-status-filter"
                        className="bg-white border rounded-[8px] px-2 py-1.5 text-[12px] font-semibold outline-none"
                        style={{ borderColor: "#E5E0DA" }}>
                        <option>All</option>
                        {STATUSES.map((s) => <option key={s}>{s}</option>)}
                    </select>
                    <label className="flex items-center gap-1.5 text-[12px] font-semibold cursor-pointer"
                           style={{ color: showOverdueOnly ? "#B33A2E" : "#6F6A64" }}>
                        <input type="checkbox" checked={showOverdueOnly}
                            onChange={(e) => setShowOverdueOnly(e.target.checked)}
                            data-testid="actions-overdue-filter"
                            className="w-3.5 h-3.5 accent-[#B33A2E]" />
                        Overdue only
                    </label>
                    <div className="ml-auto text-[11.5px]" style={{ color: "#6F6A64" }}>
                        {filtered.length} shown · {rows.length} total
                    </div>
                </div>

                {canEdit && selected.size > 0 && (
                    <div data-testid="actions-bulk-toolbar" className="flex items-center gap-3 px-4 py-2.5 border-b" style={{ borderColor: "#F0EDE8", background: "#FBEFE5" }}>
                        <span className="text-[12.5px] font-semibold" style={{ color: "#9F4212" }}>{selected.size} selected</span>
                        <button data-testid="actions-bulk-delete" onClick={bulkDelete}
                            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-[8px] text-[12px] font-semibold text-white"
                            style={{ background: "#B33A2E" }}>
                            <Trash2 className="w-3.5 h-3.5" /> Delete selected
                        </button>
                        <button data-testid="actions-bulk-clear" onClick={clearSelection}
                            className="text-[12px] font-semibold hover:underline" style={{ color: "#6F6A64" }}>Clear</button>
                    </div>
                )}

                <table className="w-full text-[12.7px]">
                    <thead>
                        <tr className="bg-[#F6F4F0] text-[10.5px] uppercase tracking-wider" style={{ color: "#6F6A64" }}>
                            {canEdit && (
                                <th className="px-4 py-2 w-9">
                                    <input type="checkbox" data-testid="actions-select-all" checked={allSelected}
                                        onChange={toggleSelectAll} className="w-3.5 h-3.5 accent-[var(--brand-accent,#C75A1B)]" />
                                </th>
                            )}
                            <SortableTh myKey="title" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-title" className="group">Action</SortableTh>
                            <SortableTh myKey="client" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-client" className="group">Client</SortableTh>
                            <SortableTh myKey="created_by_name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-created-by" className="group">Created by</SortableTh>
                            <SortableTh myKey="assignee_name" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-assignee" className="group">Assignee</SortableTh>
                            <th className="px-3 py-2 text-left">Waiting on</th>
                            <SortableTh myKey="priority" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-priority" className="group">Priority</SortableTh>
                            <SortableTh myKey="due_date" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-due" className="group">Due</SortableTh>
                            <SortableTh myKey="status" sortKey={sortKey} sortDir={sortDir} onClick={toggleSort} testId="actions-th-status" className="group">Status</SortableTh>
                            <th></th>
                        </tr>
                    </thead>
                    <tbody>
                        {sorted.map((a) => {
                            const c = clientMap[a.client_id];
                            const overdue = isOverdue(a);
                            return (
                                <tr key={a.id} data-testid={`action-row-${a.id}`}
                                    className="border-t hover:bg-[#FCFBF9]"
                                    style={{ borderColor: "#F0EDE8" }}>
                                    {canEdit && (
                                        <td className="px-4 py-3">
                                            <input type="checkbox" data-testid={`action-select-${a.id}`}
                                                checked={selected.has(a.id)} onChange={() => toggleSelect(a.id)}
                                                className="w-3.5 h-3.5 accent-[var(--brand-accent,#C75A1B)]" />
                                        </td>
                                    )}
                                    <td className="px-5 py-3">
                                        <button data-testid={`action-open-${a.id}`} onClick={() => setDetail({ open: true, id: a.id })}
                                            className="text-left group">
                                            <div className="font-semibold text-[#1F1F1F] group-hover:text-[var(--brand-accent,#C75A1B)] group-hover:underline flex items-center gap-2">
                                                {a.ref && <span data-testid={`action-ref-${a.id}`} className="font-mono text-[10.5px] font-bold px-1.5 py-0.5 rounded" style={{ background: "#FCF1DC", color: "#9F4212" }}>{a.ref}</span>}
                                                <span>{a.title}</span>
                                            </div>
                                            <div className="text-[10.5px] mt-0.5 flex items-center gap-2" style={{ color: "#9A968F" }}>
                                                {a.source ? <span>{a.source}</span> : null}
                                                {a.source_ref ? <span className="font-mono">· {a.source_ref}</span> : null}
                                                {(a.evidence?.length || 0) > 0 && (
                                                    <span className="inline-flex items-center gap-0.5"><Paperclip className="w-3 h-3" />{a.evidence.length}</span>
                                                )}
                                            </div>
                                        </button>
                                    </td>
                                    <td className="px-3 py-3">
                                        {c ? (
                                            <span className="inline-flex items-center gap-1.5">
                                                <span className="w-5 h-5 rounded-[4px] grid place-items-center text-[9px] font-bold text-white"
                                                      style={{ background: c.branding.primary_colour }}>
                                                    {c.branding.logo_initials}
                                                </span>
                                                <span className="text-[#2D2D2D]">{c.name}</span>
                                            </span>
                                        ) : <span className="italic text-[#9A968F]">—</span>}
                                    </td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">
                                        <span data-testid={`action-created-by-${a.id}`}>
                                            {a.created_by_name || <span className="italic text-[#9A968F]">—</span>}
                                        </span>
                                    </td>
                                    <td className="px-3 py-3 text-[#2D2D2D]">
                                        {(a.assignees && a.assignees.length > 0) ? (
                                            <span data-testid={`action-assignees-${a.id}`}>
                                                {a.assignees[0].name || a.assignees[0].email}
                                                {a.assignees.length > 1 && <span className="ml-1 text-[10.5px] font-semibold text-[#9F4212]">+{a.assignees.length - 1}</span>}
                                            </span>
                                        ) : a.assignee_name ? a.assignee_name : <span className="italic text-[#9A968F]">unassigned</span>}
                                    </td>
                                    <td className="px-3 py-3">
                                        {a.waiting_on_name ? (
                                            <span data-testid={`action-waiting-on-${a.id}`}
                                                className="inline-flex items-center gap-1.5 text-[11.5px] font-semibold"
                                                style={{ color: a.status === "Submitted" ? "#6B4E9E" : "#9F4212" }}
                                                title={a.status === "Submitted" ? "Awaiting review from" : "Waiting on"}>
                                                {a.status === "Submitted" ? <ShieldCheck className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                                                {a.waiting_on_name}
                                            </span>
                                        ) : <span className="italic text-[#9A968F]">—</span>}
                                    </td>
                                    <td className="px-3 py-3"><Pill tone={PRIO_TONE[a.priority] || "neutral"}>{a.priority}</Pill></td>
                                    <td className="px-3 py-3">
                                        {a.due_date ? (
                                            <span className={`inline-flex items-center gap-1 text-[11.5px] font-mono ${overdue ? "font-bold" : ""}`}
                                                  style={{ color: overdue ? "#B33A2E" : "#2D2D2D" }}>
                                                {overdue ? <AlertTriangle className="w-3 h-3" /> : <Calendar className="w-3 h-3" />}
                                                {a.due_date}
                                            </span>
                                        ) : <span className="italic text-[#9A968F]">—</span>}
                                    </td>
                                    <td className="px-3 py-3"><Pill tone={STATUS_TONE[a.status] || "neutral"}>{a.status}</Pill></td>
                                    <td className="px-3 py-3 text-right relative">
                                        {canEdit && ["Open", "In progress", "Blocked"].includes(a.status) && (
                                            <button
                                                data-testid={`action-complete-${a.id}`}
                                                onClick={() => quickComplete(a)}
                                                title="Mark complete"
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-[7px] text-[11px] font-semibold mr-1"
                                                style={{ background: "#E8F1E6", color: "#3F7D3A" }}
                                            >
                                                <CheckCircle2 className="w-3.5 h-3.5" /> Complete
                                            </button>
                                        )}
                                        {canEdit && ["Open", "In progress", "Blocked"].includes(a.status) && (
                                            <button
                                                data-testid={`action-submit-${a.id}`}
                                                onClick={() => quickSubmit(a)}
                                                title="Submit for review"
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-[7px] text-[11px] font-semibold mr-1"
                                                style={{ background: "#EEE9F6", color: "#6B4E9E" }}
                                            >
                                                <Send className="w-3.5 h-3.5" /> Submit
                                            </button>
                                        )}
                                        {isReviewer && a.status === "Submitted" && (
                                            <button
                                                data-testid={`action-verify-${a.id}`}
                                                onClick={() => quickVerify(a)}
                                                title="Verify & close"
                                                className="inline-flex items-center gap-1 px-2 py-1 rounded-[7px] text-[11px] font-semibold mr-1"
                                                style={{ background: "#E8F1E6", color: "#3F7D3A" }}
                                            >
                                                <ShieldCheck className="w-3.5 h-3.5" /> Verify
                                            </button>
                                        )}
                                        {canEdit && (
                                            <>
                                                <button
                                                    data-testid={`action-row-menu-${a.id}`}
                                                    onClick={() => setMenuRowId(menuRowId === a.id ? null : a.id)}
                                                    className="w-8 h-8 rounded-[7px] grid place-items-center hover:bg-[#F6F4F0]">
                                                    <MoreVertical className="w-4 h-4" style={{ color: "#6F6A64" }} />
                                                </button>
                                                {menuRowId === a.id && (
                                                    <div className="absolute right-3 top-[80%] bg-white border rounded-lg shadow-brand min-w-[160px] py-1 z-30"
                                                         style={{ borderColor: "#E5E0DA" }}>
                                                        <button onClick={() => { setDetail({ open: true, id: a.id }); setMenuRowId(null); }}
                                                            className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]">
                                                            <FileText className="w-3.5 h-3.5" /> Details &amp; review
                                                        </button>
                                                        <button onClick={() => { setDialog({ open: true, mode: "edit", initial: a }); setMenuRowId(null); }}
                                                            className="w-full text-left flex items-center gap-2 px-3 py-2 text-[12.5px] hover:bg-[#F6F4F0]">
                                                            <Pencil className="w-3.5 h-3.5" /> Edit
                                                        </button>
                                                    </div>
                                                )}
                                            </>
                                        )}
                                    </td>
                                </tr>
                            );
                        })}
                        {sorted.length === 0 && (
                            <tr><td colSpan={canEdit ? 10 : 9} className="px-5 py-10 text-center italic" style={{ color: "#9A968F" }}>
                                <CheckSquare className="w-5 h-5 mx-auto mb-2 opacity-60" />
                                No actions match the filters.
                            </td></tr>
                        )}
                    </tbody>
                </table>
            </div>

            <ActionDialog open={dialog.open} mode={dialog.mode} initial={dialog.initial} prefill={dialog.prefill}
                clients={clients} canAskAi={user?.role !== "site_manager"} onClose={() => setDialog((d) => ({ ...d, open: false }))}
                onSaved={refresh} />

            <ActionDetailDialog open={detail.open} actionId={detail.id} clients={clients}
                canEdit={canEdit} isReviewer={isReviewer}
                onEdit={(a) => { setDetail({ open: false, id: null }); setDialog({ open: true, mode: "edit", initial: a }); }}
                onClose={() => { setDetail({ open: false, id: null }); if (returnTo) { const t = returnTo; setReturnTo(null); navigate(t); } }} onChanged={refresh} />
        </div>
    );
}

function AssigneePicker({ value = [], onChange, clientId, clientName }) {
    const [pool, setPool] = useState({ internal: [], portal: [] });
    const [open, setOpen] = useState(false);
    const [q, setQ] = useState("");
    const [extEmail, setExtEmail] = useState("");
    const [extName, setExtName] = useState("");

    useEffect(() => {
        if (!clientId) { setPool({ internal: [], portal: [] }); return; }
        api.get(`/actions/assignable-users`, { params: { client_id: clientId } })
            .then((r) => setPool(r.data)).catch(() => setPool({ internal: [], portal: [] }));
    }, [clientId]);

    const selectedEmails = new Set((value || []).map((a) => (a.email || "").toLowerCase()));
    function toggle(u) {
        const em = (u.email || "").toLowerCase();
        if (selectedEmails.has(em)) onChange((value || []).filter((a) => (a.email || "").toLowerCase() !== em));
        else onChange([...(value || []), { user_id: u.user_id, name: u.name, email: u.email, type: u.type }]);
    }
    function removeAt(em) { onChange((value || []).filter((a) => (a.email || "").toLowerCase() !== em.toLowerCase())); }
    function addExternal() {
        const em = extEmail.trim().toLowerCase();
        if (!em || !/^[^@\s]+@[^@\s]+\.[^@\s]+$/.test(em)) { toast.error("Enter a valid email"); return; }
        if (selectedEmails.has(em)) { toast.error("Already added"); return; }
        onChange([...(value || []), { user_id: null, name: extName.trim() || null, email: em, type: "external" }]);
        setExtEmail(""); setExtName("");
    }
    const match = (u) => !q || `${u.name || ""} ${u.email || ""}`.toLowerCase().includes(q.toLowerCase());
    const internal = pool.internal.filter(match);
    const portal = pool.portal.filter(match);

    return (
        <div data-testid="assignee-picker">
            {(value || []).length > 0 && (
                <div className="flex flex-wrap gap-1.5 mb-2">
                    {value.map((a) => (
                        <span key={a.email} data-testid={`assignee-chip-${a.email}`} className="inline-flex items-center gap-1.5 pl-2 pr-1 py-1 rounded-full text-[11.5px] font-semibold"
                            style={{ background: a.type === "external" ? "#EEF3FB" : a.type === "portal" ? "#E9F3EE" : "#FBEFE5", color: a.type === "external" ? "#1F6FE5" : a.type === "portal" ? "#2E7D5B" : "#9F4212" }}>
                            {a.type === "external" ? <Mail className="w-3 h-3" /> : a.type === "portal" ? <Building2 className="w-3 h-3" /> : <Users className="w-3 h-3" />}
                            {a.name || a.email}
                            <button type="button" data-testid={`assignee-remove-${a.email}`} onClick={() => removeAt(a.email)} className="w-4 h-4 rounded-full grid place-items-center hover:bg-black/10"><X className="w-3 h-3" /></button>
                        </span>
                    ))}
                </div>
            )}
            <button type="button" data-testid="assignee-picker-toggle" onClick={() => setOpen((v) => !v)} disabled={!clientId}
                className="fi text-left flex items-center justify-between disabled:opacity-50" >
                <span style={{ color: (value || []).length ? "#1F1F1F" : "#9A968F" }}>
                    {clientId ? ((value || []).length ? `${value.length} assigned — add more` : "Select assignees…") : "Choose a client first"}
                </span>
                <Plus className="w-3.5 h-3.5" style={{ color: "#6F6A64" }} />
            </button>
            {open && clientId && (
                <div data-testid="assignee-picker-panel" className="mt-1.5 border rounded-[10px] bg-white shadow-brand-sm overflow-hidden" style={{ borderColor: "#E5E0DA" }}>
                    <div className="flex items-center gap-2 px-3 py-2 border-b" style={{ borderColor: "#F0EDE8" }}>
                        <Search className="w-3.5 h-3.5" style={{ color: "#9A968F" }} />
                        <input data-testid="assignee-search" autoFocus value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search people…" className="flex-1 bg-transparent outline-none text-[12px]" />
                    </div>
                    <div className="max-h-[190px] overflow-y-auto py-1">
                        {internal.length > 0 && <div className="px-3 pt-1.5 pb-1 text-[9.5px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>Priority Risk staff</div>}
                        {internal.map((u) => <PickRow key={u.user_id} u={u} selected={selectedEmails.has((u.email || "").toLowerCase())} onClick={() => toggle(u)} />)}
                        {portal.length > 0 && <div className="px-3 pt-2 pb-1 text-[9.5px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>{clientName || "Client"} portal users</div>}
                        {portal.map((u) => <PickRow key={u.user_id} u={u} selected={selectedEmails.has((u.email || "").toLowerCase())} onClick={() => toggle(u)} />)}
                        {internal.length === 0 && portal.length === 0 && <div className="px-3 py-3 text-[11.5px] italic" style={{ color: "#9A968F" }}>No matching users.</div>}
                    </div>
                    <div className="border-t px-3 py-2.5 space-y-1.5" style={{ borderColor: "#F0EDE8", background: "#FCFBF9" }}>
                        <div className="text-[9.5px] font-bold uppercase tracking-wider" style={{ color: "#9A968F" }}>Add someone by email</div>
                        <div className="flex items-center gap-1.5">
                            <input data-testid="assignee-ext-name" value={extName} onChange={(e) => setExtName(e.target.value)} placeholder="Name (optional)" className="fi flex-1 !py-1.5" />
                            <input data-testid="assignee-ext-email" value={extEmail} onChange={(e) => setExtEmail(e.target.value)} onKeyDown={(e) => e.key === "Enter" && (e.preventDefault(), addExternal())} placeholder="email@company.com" className="fi flex-1 !py-1.5" />
                            <button type="button" data-testid="assignee-ext-add" onClick={addExternal} className="px-3 py-1.5 rounded-[8px] text-[11.5px] font-semibold text-white whitespace-nowrap" style={{ background: "var(--brand-accent,#C75A1B)" }}>Add</button>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
}

function PickRow({ u, selected, onClick }) {
    return (
        <button type="button" data-testid={`assignee-option-${u.email}`} onClick={onClick}
            className="w-full flex items-center gap-2 px-3 py-1.5 text-left hover:bg-[#FCFBF9]">
            <span className="w-4 h-4 rounded-[4px] border grid place-items-center flex-shrink-0" style={{ borderColor: selected ? "var(--brand-accent,#C75A1B)" : "#CFC9C1", background: selected ? "var(--brand-accent,#C75A1B)" : "#fff" }}>
                {selected && <Check className="w-3 h-3 text-white" />}
            </span>
            <span className="min-w-0 flex-1">
                <span className="block text-[12px] font-semibold text-[#1F1F1F] truncate">{u.name}</span>
                <span className="block text-[10.5px] truncate" style={{ color: "#9A968F" }}>{u.email}</span>
            </span>
        </button>
    );
}

function ActionDialog({ open, mode = "create", initial, prefill, clients, onClose, onSaved, canAskAi = true }) {
    const [form, setForm] = useState({});
    const [busy, setBusy] = useState(false);
    const [attachments, setAttachments] = useState([]);
    const { portalClientId, isInternalView } = useView();
    const navigate = useNavigate();

    useEffect(() => {
        const base = initial ? { ...initial } : {
            title: "", description: "",
            client_id: prefill?.client_id || ((!isInternalView && portalClientId) ? portalClientId : ""),
            project_id: prefill?.project_id || undefined,
            priority: "Medium", status: "Open", source: "Manual",
            due_date: "",
        };
        if (!base.assignees || base.assignees.length === 0) {
            base.assignees = base.assignee_email
                ? [{ user_id: null, name: base.assignee_name || null, email: base.assignee_email, type: "external" }]
                : [];
        }
        setForm(base);
        setAttachments([]);
    }, [initial, open, prefill, portalClientId, isInternalView]);

    if (!open) return null;

    function set(k, v) { setForm((f) => ({ ...f, [k]: v })); }

    function addFiles(e) {
        const picked = Array.from(e.target.files || []);
        const tooBig = picked.filter((f) => f.size > 15 * 1024 * 1024);
        if (tooBig.length) toast.error(`${tooBig[0].name} is over 15 MB and was skipped`);
        setAttachments((prev) => [...prev, ...picked.filter((f) => f.size <= 15 * 1024 * 1024)]);
        e.target.value = "";
    }
    function removeFile(idx) { setAttachments((prev) => prev.filter((_, i) => i !== idx)); }

    async function save() {
        if (!form.title?.trim()) { toast.error("Action title is required"); return; }
        if (!form.client_id) { toast.error("Please choose a client"); return; }
        const cleaned = Object.fromEntries(
            Object.entries(form).filter(([, v]) => !(typeof v === "string" && v.trim() === ""))
        );
        // Don't send an empty assignees array — that would clobber a legacy assignee.
        if (Array.isArray(cleaned.assignees) && cleaned.assignees.length === 0) delete cleaned.assignees;
        setBusy(true);
        try {
            let actionId = initial?.id;
            if (mode === "create") {
                const { data } = await api.post("/actions", cleaned);
                actionId = data.id;
                toast.success(`Created action: ${data.title}`);
            } else {
                const { data } = await api.patch(`/actions/${initial.id}`, cleaned);
                toast.success(`Updated: ${data.title}`);
            }
            // Upload any attached documents/images to the (now-saved) action.
            for (const file of attachments) {
                const fd = new FormData();
                fd.append("file", file);
                try {
                    await api.post(`/actions/${actionId}/evidence`, fd, { headers: { "Content-Type": "multipart/form-data" } });
                } catch {
                    toast.error(`Could not attach ${file.name}`);
                }
            }
            onSaved?.();
            onClose?.();
        } catch (e) {
            toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
        } finally {
            setBusy(false);
        }
    }

    return (
        <div className="fixed inset-0 z-50 grid place-items-center p-4 animate-fade-in"
             style={{ background: "rgba(26,26,26,.45)" }}>
            <div data-testid="action-form-dialog"
                className="bg-white rounded-[14px] shadow-brand-lg w-full max-w-[620px] max-h-[92vh] flex flex-col border"
                style={{ borderColor: "#E5E0DA" }}>
                <div className="px-6 pt-5 pb-4 border-b flex items-start gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="w-10 h-10 rounded-[10px] grid place-items-center"
                         style={{ background: "#FCF1DC", color: "#C68A1C" }}>
                        <CheckSquare className="w-5 h-5" />
                    </div>
                    <div className="flex-1 min-w-0">
                        <h2 className="font-display text-[19px] font-bold text-[#1F1F1F]">
                            {mode === "create" ? "New action" : `Edit action`}
                        </h2>
                        <p className="text-[12px] mt-0.5" style={{ color: "#6F6A64" }}>
                            Track follow-ups from audits, inspections, incidents and resident concerns.
                        </p>
                    </div>
                    <button data-testid="action-form-close" onClick={onClose} className="w-9 h-9 rounded-[8px] hover:bg-[#F6F4F0] grid place-items-center">
                        <X className="w-4 h-4" />
                    </button>
                </div>
                {mode === "edit" && canAskAi && initial?.id && (
                    <div className="px-6 pt-3">
                        <button data-testid={`action-ask-ai-${initial.id}`}
                            onClick={() => navigate(`/app/ai-assist?context_type=action&context_id=${initial.id}&context_label=${encodeURIComponent("Action: " + (initial.title || ""))}`)}
                            className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-full bg-[#EEF3FB] text-[#1F6FE5] text-[11px] font-semibold hover:bg-[#E0ECFB]" title="Ask Beyond AI about this action">
                            <Sparkles className="w-3 h-3" /> Ask Beyond AI
                        </button>
                    </div>
                )}

                <div className="px-6 py-5 flex-1 overflow-y-auto space-y-4 form-pane2">
                    {form.project_id && (
                        <div data-testid="action-project-link" className="flex items-center gap-2 text-[11.5px] font-semibold px-3 py-2 rounded-lg" style={{ background: "#FBEFE5", color: "#9F4212" }}>
                            <FolderOpen className="w-3.5 h-3.5" /> Linked to project{prefill?.project_label ? `: ${prefill.project_label}` : ""}
                        </div>
                    )}
                    <Field label="Title" required>
                        <input data-testid="action-form-title" value={form.title || ""} onChange={(e) => set("title", e.target.value)} className="fi" />
                    </Field>
                    <Field label="Description">
                        <textarea data-testid="action-form-desc" rows={2} value={form.description || ""} onChange={(e) => set("description", e.target.value)} className="fi" />
                    </Field>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Client" required>
                            <select data-testid="action-form-client" value={form.client_id || ""} disabled={!!form.project_id} onChange={(e) => set("client_id", e.target.value)} className="fi">
                                <option value="">Select…</option>
                                {clients.map((c) => <option key={c.id} value={c.id}>{c.name}</option>)}
                            </select>
                        </Field>
                        <Field label="Due date">
                            <input data-testid="action-form-due" type="date" value={form.due_date || ""} onChange={(e) => set("due_date", e.target.value)} className="fi" />
                        </Field>
                    </div>
                    <Field label="Assignees">
                        <AssigneePicker value={form.assignees || []} onChange={(a) => set("assignees", a)}
                            clientId={form.client_id} clientName={clients.find((c) => c.id === form.client_id)?.name} />
                    </Field>
                    <div className="grid sm:grid-cols-3 gap-3">
                        <Field label="Priority">
                            <select data-testid="action-form-priority" value={form.priority || "Medium"} onChange={(e) => set("priority", e.target.value)} className="fi">
                                {PRIORITIES.map((p) => <option key={p}>{p}</option>)}
                            </select>
                        </Field>
                        <Field label="Status">
                            <select data-testid="action-form-status" value={form.status || "Open"} onChange={(e) => set("status", e.target.value)} className="fi">
                                {STATUSES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                        <Field label="Source">
                            <select data-testid="action-form-source" value={form.source || "Manual"} onChange={(e) => set("source", e.target.value)} className="fi">
                                {SOURCES.map((s) => <option key={s}>{s}</option>)}
                            </select>
                        </Field>
                    </div>
                    <div className="rounded-[10px] border p-3" style={{ borderColor: "#E5E0DA", background: "#FCFBF9" }}>
                        <label className="flex items-center gap-2 text-[12.5px] font-semibold text-[#1F1F1F]">
                            <input data-testid="action-form-overdue-reminders" type="checkbox"
                                checked={form.overdue_reminders_enabled !== false}
                                onChange={(e) => set("overdue_reminders_enabled", e.target.checked)} />
                            <BellRing className="w-3.5 h-3.5" style={{ color: "var(--brand-accent,#C75A1B)" }} /> Chase this action when overdue
                        </label>
                        <p data-testid="action-form-overdue-callout" className="text-[11px] mt-1.5 leading-snug" style={{ color: "#6F6A64" }}>
                            {form.overdue_reminders_enabled === false
                                ? "Overdue reminder emails are turned off for this action."
                                : (form.due_date
                                    ? "If this action passes its due date, we'll email the assignees and you (the creator) on a repeating cadence set by its priority — until it's completed."
                                    : "Set a due date and we'll email the assignees and you (the creator) automatically once it's overdue, repeating by priority until it's done.")}
                        </p>
                    </div>
                    <div className="grid sm:grid-cols-2 gap-3">
                        <Field label="Source reference">
                            <input data-testid="action-form-source-ref" value={form.source_ref || ""} onChange={(e) => set("source_ref", e.target.value)} className="fi font-mono" placeholder="e.g. AUD-024" />
                        </Field>
                    </div>
                    <Field label="Attachments">
                        <label data-testid="action-form-attach" className="inline-flex items-center gap-1.5 px-3 py-2 rounded-[8px] text-[12px] font-semibold cursor-pointer border bg-white hover:bg-[#F6F4F0]" style={{ borderColor: "#E5E0DA", color: "#6F6A64" }}>
                            <Upload className="w-3.5 h-3.5" /> Attach documents or images
                            <input type="file" multiple className="hidden" onChange={addFiles} />
                        </label>
                        {attachments.length > 0 && (
                            <ul className="mt-2 space-y-1.5">
                                {attachments.map((f, i) => (
                                    <li key={i} data-testid={`action-form-attachment-${i}`} className="flex items-center gap-2.5 px-3 py-2 rounded-lg border" style={{ borderColor: "#E5E0DA" }}>
                                        <FileText className="w-4 h-4 shrink-0" style={{ color: "var(--brand-accent,#C75A1B)" }} />
                                        <div className="flex-1 min-w-0">
                                            <div className="text-[12px] font-medium text-[#1F1F1F] truncate">{f.name}</div>
                                            <div className="text-[10.5px]" style={{ color: "#9A968F" }}>{Math.round((f.size || 0) / 1024)} KB</div>
                                        </div>
                                        <button type="button" data-testid={`action-form-attachment-remove-${i}`} onClick={() => removeFile(i)} className="w-7 h-7 rounded-[6px] grid place-items-center hover:bg-[#FBE8E5]" title="Remove"><Trash2 className="w-3.5 h-3.5" style={{ color: "#B33A2E" }} /></button>
                                    </li>
                                ))}
                            </ul>
                        )}
                        {mode === "edit" && (initial?.evidence?.length || 0) > 0 && (
                            <p className="text-[10.5px] mt-1.5" style={{ color: "#9A968F" }}>
                                {initial.evidence.length} file(s) already attached — manage them from the action's Details view.
                            </p>
                        )}
                    </Field>
                </div>

                <div className="px-6 py-4 border-t flex items-center gap-3" style={{ borderColor: "#F0EDE8" }}>
                    <div className="flex-1" />
                    <button onClick={onClose} disabled={busy} data-testid="action-form-cancel"
                        className="px-4 py-2 rounded-[8px] text-[12.5px] font-semibold border bg-white"
                        style={{ borderColor: "#E5E0DA" }}>Cancel</button>
                    <button onClick={save} disabled={busy} data-testid="action-form-save"
                        className="inline-flex items-center gap-1.5 px-4 py-2 rounded-[8px] text-[12.5px] font-semibold text-white"
                        style={{ background: "var(--brand-accent, #C75A1B)" }}>
                        {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                        {busy ? "Saving…" : "Save action"}
                    </button>
                </div>

                <style>{`
                    .form-pane2 .fi {
                        width: 100%; padding: 8px 11px; background: #fff; border: 1px solid #E5E0DA;
                        border-radius: 8px; font-size: 12.5px; color: #1F1F1F; outline: none;
                        transition: border-color .12s, box-shadow .12s;
                    }
                    .form-pane2 .fi:focus { border-color: var(--brand-accent, #C75A1B); box-shadow: 0 0 0 3px #FBEFE5; }
                `}</style>
            </div>
        </div>
    );
}

function Field({ label, required, children }) {
    return (
        <label className="block">
            <span className="text-[11px] font-semibold mb-1 block" style={{ color: "#6F6A64" }}>
                {label} {required ? <span className="text-[#B33A2E]">*</span> : null}
            </span>
            {children}
        </label>
    );
}
